import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * The slice of {@link AnalyserNode} the meter reads. Declared narrowly so a demo
 * or test can supply a spectrum stand-in without fabricating a whole Web Audio
 * node; a real `AnalyserNode` satisfies it as-is.
 */
export interface AudioSpectrumSource {
  readonly frequencyBinCount: number;
  getByteFrequencyData(array: Uint8Array<ArrayBuffer>): void;
}

interface AudioMeterOwnProps {
  /**
   * Spectrum source to read live audio from. When null/undefined the meter shows
   * the idle pulse animation (useful before mic acquisition or in
   * presentational/demo contexts).
   */
  analyser?: AudioSpectrumSource | null;
  /**
   * Whether the animation loop runs. Typically tied to the "recording" state.
   * When false the loop is torn down and the meter renders a static baseline.
   * @default true
   */
  active?: boolean;
  /** Number of columns in the dot matrix. @default 7 */
  columns?: number;
  /** Number of rows in the dot matrix (kept odd for a center row). @default 7 */
  rows?: number;
  /** Distance in px between dot centers. @default 6 */
  pitch?: number;
  /** Square dot size in px. @default 4 */
  dotSize?: number;
  ref?: React.Ref<HTMLCanvasElement>;
}

export type AudioMeterProps = AudioMeterOwnProps &
  Omit<React.ComponentPropsWithoutRef<'canvas'>, keyof AudioMeterOwnProps>;

const DEFAULT_ROWS = 7;
const DEFAULT_PITCH = 6; // px between dot centers (→ 2px gap with 4px squares)
const DEFAULT_DOT = 4;
const IDLE_STEP = 0.3; // seconds per idle-pulse step
const SILENT_THRESH = 0.25; // max level below which we show the idle pulse

// FFT mapping constants
const MIN_BIN = 2; // skip DC / rumble
const MAX_BIN = 140; // upper edge of the vocal band we care about

// Fallbacks used when CSS custom properties can't be resolved (SSR / jsdom).
const FALLBACK_ACTIVE = '#33221A'; // brand-brown-dark
const FALLBACK_INACTIVE = '#E8E4E3'; // neutral-200

function noop() {}

/**
 * AudioMeter renders a fixed-grid, square-dot spectrum meter on a canvas.
 *
 * It is purely presentational: pass an {@link AnalyserNode} (e.g. from
 * `AudioStreamService.getAnalyser()`) and it visualizes the live spectrum with
 * a log-spaced, spectrally-tilted mapping plus a decaying peak trail. When no
 * analyser is supplied, or the signal is silent, it shows a two-tone pulse
 * emanating from the center outward.
 *
 * Colors are driven by the `--audio-meter-active` / `--audio-meter-inactive`
 * CSS custom properties (defaulted to design tokens in styles.css), read at
 * draw time so the meter follows the active theme.
 */
export function AudioMeter({
  analyser,
  active = true,
  columns = 7,
  rows = DEFAULT_ROWS,
  pitch = DEFAULT_PITCH,
  dotSize = DEFAULT_DOT,
  className,
  ref,
  ...props
}: AudioMeterProps) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const assignRef = React.useCallback(
    (node: HTMLCanvasElement | null) => {
      canvasRef.current = node;
      // oxlint-disable-next-line anti-slop/no-runtime-typeof -- React.Ref is a bare callback|object union with no discriminant, so typeof-function is the only split available.
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  // Latest props for the animation loop, synced after commit so the loop
  // never reads a value from a render React went on to discard.
  const analyserRef = React.useRef(analyser);
  const columnsRef = React.useRef(columns);
  const rowsRef = React.useRef(rows);
  const pitchRef = React.useRef(pitch);
  const dotRef = React.useRef(dotSize);
  React.useEffect(() => {
    analyserRef.current = analyser;
    columnsRef.current = columns;
    rowsRef.current = rows;
    pitchRef.current = pitch;
    dotRef.current = dotSize;
  });

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      return noop;
    }

    let raf = 0;
    let last = 0;
    let idleAccum = 0;
    let idlePos = 0;
    let silent = true;
    let cssW = 0;
    let cssH = 0;
    const level: number[] = [];
    const peak: number[] = [];
    let freq: Uint8Array<ArrayBuffer> | null = null;
    let colors = { active: FALLBACK_ACTIVE, inactive: FALLBACK_INACTIVE };

    const readColors = () => {
      const style = getComputedStyle(canvas);
      const a = style.getPropertyValue('--audio-meter-active').trim();
      const i = style.getPropertyValue('--audio-meter-inactive').trim();
      colors = {
        active: a || FALLBACK_ACTIVE,
        inactive: i || FALLBACK_INACTIVE,
      };
    };

    const setup = () => {
      const dpr = window.devicePixelRatio || 1;
      cssW = canvas.clientWidth;
      cssH = canvas.clientHeight;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cols = columnsRef.current;
      for (let c = 0; c < cols; c++) {
        if (level[c] === undefined) {
          level[c] = 0;
          peak[c] = 0;
        }
      }
      readColors();
    };

    const draw = () => {
      const cols = columnsRef.current;
      ctx.clearRect(0, 0, cssW, cssH);
      if (cols < 1 || cssW < 1) {
        return;
      }
      const pitchPx = pitchRef.current;
      const sq = dotRef.current;
      const off = sq / 2;
      const half = (rowsRef.current - 1) / 2; // rows above/below center
      const blockW = cols * pitchPx;
      const startX = (cssW - blockW) / 2;
      const cy = cssH / 2;
      const center = Math.round((cols - 1) / 2);
      const maxDist = Math.max(center, cols - 1 - center);
      const period = maxDist + 2;
      const r = ((idlePos % period) + period) % period;

      for (let c = 0; c < cols; c++) {
        const x = startX + (c + 0.5) * pitchPx - off;
        const lit = Math.round(level[c] * half);
        const pk = Math.round(peak[c] * half);

        let centerCol: string;
        if (lit >= 1) {
          centerCol = colors.active;
        } else if (silent) {
          centerCol = Math.abs(c - center) === r ? colors.active : colors.inactive;
        } else {
          centerCol = colors.inactive;
        }
        ctx.fillStyle = centerCol;
        ctx.fillRect(x, cy - off, sq, sq);

        for (let k = 1; k <= half; k++) {
          const col = k <= lit ? colors.active : k <= pk ? colors.inactive : null;
          if (!col) {
            continue;
          }
          ctx.fillStyle = col;
          ctx.fillRect(x, cy - k * pitchPx - off, sq, sq);
          ctx.fillRect(x, cy + k * pitchPx - off, sq, sq);
        }
      }
    };

    const frame = (now: number) => {
      if (!last) {
        last = now;
      }
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      if (cssW !== canvas.clientWidth) {
        setup();
      }
      const cols = columnsRef.current;
      const node = analyserRef.current;
      let mx = 0;

      if (node) {
        if (!freq || freq.length !== node.frequencyBinCount) {
          freq = new Uint8Array(new ArrayBuffer(node.frequencyBinCount));
        }
        node.getByteFrequencyData(freq);
        const maxBin = Math.min(freq.length - 1, MAX_BIN);
        const lMin = Math.log(MIN_BIN);
        const lMax = Math.log(maxBin);
        for (let c = 0; c < cols; c++) {
          const b0 = Math.floor(Math.exp(lMin + (lMax - lMin) * (c / cols)));
          let b1 = Math.floor(Math.exp(lMin + (lMax - lMin) * ((c + 1) / cols)));
          if (b1 <= b0) {
            b1 = b0 + 1;
          }
          let sum = 0;
          let n = 0;
          for (let b = b0; b < b1 && b < freq.length; b++) {
            sum += freq[b];
            n++;
          }
          let mag = n ? sum / n / 255 : 0;
          const t = cols > 1 ? c / (cols - 1) : 0;
          mag *= 0.45 + 1.95 * t; // spectral tilt: attenuate lows, boost highs
          mag = Math.max(0, mag - 0.08) * 1.15; // noise gate + gain
          const v = Math.min(1, mag);
          const a = v > level[c] ? 0.5 : 0.2; // fast attack / slow release
          level[c] += (v - level[c]) * a;
          if (level[c] > peak[c]) {
            peak[c] = level[c];
          } else {
            peak[c] -= dt * 0.7;
            if (peak[c] < 0) {
              peak[c] = 0;
            }
          }
          if (level[c] > mx) {
            mx = level[c];
          }
        }
      } else {
        for (let c = 0; c < cols; c++) {
          level[c] += (0 - level[c]) * 0.2;
          peak[c] = 0;
        }
      }

      silent = mx < SILENT_THRESH;
      idleAccum += dt;
      if (idleAccum >= IDLE_STEP) {
        idleAccum -= IDLE_STEP;
        idlePos++;
      }
      draw();
      raf = requestAnimationFrame(frame);
    };

    setup();

    let resizeObserver: ResizeObserver | undefined;
    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- Environment feature detection for a global that jsdom does not define; there is no value to parse at a boundary.
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        setup();
        if (!active) {
          draw();
        }
      });
      resizeObserver.observe(canvas);
    }

    if (active) {
      last = 0;
      raf = requestAnimationFrame(frame);
    } else {
      for (let c = 0; c < columnsRef.current; c++) {
        level[c] = 0;
        peak[c] = 0;
      }
      silent = false;
      draw();
    }

    return () => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      resizeObserver?.disconnect();
    };
  }, [active]);

  return (
    <canvas
      ref={assignRef}
      aria-hidden="true"
      className={cn('audio-meter block size-full', className)}
      {...props}
    />
  );
}
